import { useState, useEffect } from "react";
import { Check, ChevronDown, Loader2, Clock } from "lucide-react";
import { STEP_STATUS } from "@/data/mockData";

const STATUS_META = {
  [STEP_STATUS.DONE]: {
    label: "Terminé",
    color: "#22C55E",
    ring: "bg-[#111111] text-white",
    Icon: Check,
  },
  [STEP_STATUS.IN_PROGRESS]: {
    label: "En cours",
    color: "#111111",
    ring: "bg-[#C8FF2F] text-[#111111]",
    Icon: Loader2,
    animate: true,
  },
  [STEP_STATUS.BLOCKED]: {
    label: "En attente",
    color: "#F59E0B",
    ring: "bg-[#FEF3C7] text-[#92400E]",
    Icon: Clock,
  },
  [STEP_STATUS.UPCOMING]: {
    label: "À venir",
    color: "#D1D5DB",
    ring: "bg-white border border-[#EEF2F5] text-[#9CA3AF]",
    Icon: () => (
      <span className="h-1.5 w-1.5 rounded-full bg-[#D1D5DB]" />
    ),
  },
};

// Messages simples (fallback si le backend ne fournit pas de client_message)
const FALLBACK_STEP_MESSAGES = {
  reservation: "Votre véhicule est officiellement réservé.",
  acompte: "Votre acompte a bien été enregistré.",
  admin: "Votre dossier administratif est en cours de constitution.",
  check: "Nos équipes vérifient votre véhicule dans les moindres détails.",
  ct: "Le contrôle technique est en cours de validation.",
  esthetique: "Notre équipe prépare votre véhicule pour la livraison.",
  pret: "Votre véhicule est prêt pour la remise.",
  livraison: "Nous préparons le rendez-vous de livraison.",
  cpi: "Votre certificat provisoire sera bientôt disponible.",
  immat: "L'immatriculation de votre véhicule est en cours.",
  paiement: "Nous attendons la finalisation du paiement.",
};

const stepDescription = (step) => {
  if (step.status === STEP_STATUS.BLOCKED) {
    // Reformulation positive côté client (jamais la reason interne)
    return "Nous finalisons cette étape et vous tenons informé dès qu'elle est validée.";
  }
  return step.client_message ||
    FALLBACK_STEP_MESSAGES[step.key] ||
    "Étape de la préparation de votre véhicule.";
};

const StepItem = ({ step, expanded, onToggle }) => {
  const m = STATUS_META[step.status] || STATUS_META[STEP_STATUS.UPCOMING];
  const Icon = m.Icon;
  const isUpcoming = step.status === STEP_STATUS.UPCOMING;
  const isDone = step.status === STEP_STATUS.DONE;

  return (
    <li className="relative">
      <button
        onClick={onToggle}
        data-testid={`client-step-${step.key}`}
        className="w-full flex items-start gap-4 py-4 text-left group"
      >
        {/* Node */}
        <div className="relative shrink-0">
          <div
            className={[
              "h-9 w-9 rounded-full flex items-center justify-center transition-all",
              m.ring,
              isDone ? "animate-check-pop" : "",
            ].join(" ")}
          >
            <Icon size={14} strokeWidth={2.5} className={m.animate ? "animate-spin-slow" : ""} />
          </div>
        </div>

        <div className="flex-1 min-w-0 pt-1.5">
          <div className="flex items-center justify-between gap-3">
            <p
              className={[
                "text-[15px] font-semibold tracking-tight leading-tight",
                isUpcoming ? "text-[#9CA3AF]" : "text-[#111111]",
              ].join(" ")}
            >
              {step.label}
            </p>
            <ChevronDown
              size={15}
              strokeWidth={2}
              className={[
                "shrink-0 text-[#9CA3AF] transition-transform duration-300",
                expanded ? "rotate-180 text-[#111111]" : "",
              ].join(" ")}
            />
          </div>
          <p
            className="mt-0.5 text-[11.5px] font-medium"
            style={{ color: m.color }}
          >
            {m.label}
          </p>

          {/* Expanded content */}
          <div
            className={[
              "grid transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
              expanded
                ? "grid-rows-[1fr] opacity-100 mt-3"
                : "grid-rows-[0fr] opacity-0 mt-0",
            ].join(" ")}
          >
            <div className="overflow-hidden">
              <p className="text-[13.5px] leading-relaxed text-[#374151]">
                {stepDescription(step)}
              </p>
            </div>
          </div>
        </div>
      </button>
    </li>
  );
};

const ClientTimeline = ({ steps }) => {
  // par défaut : ouvrir l'étape en cours ou la dernière terminée
  const defaultKey =
    steps.find((s) => s.status === STEP_STATUS.IN_PROGRESS)?.key ||
    [...steps].reverse().find((s) => s.status === STEP_STATUS.DONE)?.key ||
    steps[0].key;
  const [openKey, setOpenKey] = useState(defaultKey);

  useEffect(() => {
    setOpenKey(defaultKey);
  }, [defaultKey]);

  return (
    <section data-testid="client-suivi" className="wyvio-fade-in">
      <h2 className="text-[26px] font-semibold tracking-tight text-[#111111]">
        Suivi
      </h2>
      <p className="mt-1.5 text-[13.5px] text-[#6B7280] leading-relaxed">
        Voici toutes les étapes de la préparation de votre véhicule. Nous vous tenons informé à chaque avancée.
      </p>

      <ol className="mt-6 relative">
        {/* Ligne verticale */}
        <span
          aria-hidden
          className="absolute left-[18px] top-6 bottom-6 w-px bg-[#EEF2F5]"
        />

        {steps.map((step) => (
          <StepItem
            key={step.key}
            step={step}
            expanded={openKey === step.key}
            onToggle={() =>
              setOpenKey((k) => (k === step.key ? null : step.key))
            }
          />
        ))}
      </ol>
    </section>
  );
};

export default ClientTimeline;
